import { useState } from 'react';
import BioStampCard from '@/components/feature/BioStampCard';
import { useInView } from '@/hooks/useInView';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db69e44be24agvuu3h90';

function DeviceMockup() {
  return (
    <div className="relative w-full max-w-[520px]">
      {/* Laptop */}
      <div className="rounded-2xl bg-foreground-950 p-2.5 md:p-3">
        <div className="rounded-xl bg-background-50 overflow-hidden">
          {/* Browser bar */}
          <div className="flex items-center gap-1.5 px-3 py-2 bg-background-100 border-b border-background-200/70">
            <span className="w-2 h-2 rounded-full bg-background-300" />
            <span className="w-2 h-2 rounded-full bg-background-300" />
            <span className="w-2 h-2 rounded-full bg-background-300" />
            <span className="ml-2 text-[10px] text-foreground-500">bioVerifier</span>
          </div>
          {/* Screen body */}
          <div className="p-4">
            <p className="text-xs font-semibold text-foreground-900 mb-3">
              Your bioVerifier Test Message
            </p>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-mail-line text-primary-600 text-xs" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-foreground-900">demo@bio-mail.com</p>
                <p className="text-[9px] text-foreground-500">Registered sender</p>
              </div>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-2 mb-3">
              <BioStampCard className="h-16 md:h-20" />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-100">
              <i className="ri-checkbox-circle-fill text-accent-600 text-sm" />
              <span className="text-[11px] font-semibold text-accent-700">Verified</span>
              <span className="text-[10px] text-accent-700/80">Sender identity confirmed</span>
            </div>
          </div>
        </div>
      </div>
      {/* Laptop base */}
      <div className="mx-auto w-[62%] h-2 bg-foreground-900 rounded-b-xl" />

      {/* Phone */}
      <div className="hidden sm:block absolute -right-2 md:-right-4 -bottom-6 w-[120px] rounded-2xl bg-foreground-950 p-1.5">
        <div className="rounded-xl bg-background-50 p-3 text-center">
          <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-2">
            <i className="ri-shield-check-fill text-accent-600 text-lg" />
          </div>
          <p className="text-xs font-semibold text-foreground-900 mb-1">Verified</p>
          <p className="text-[9px] text-foreground-500 leading-relaxed">
            Secure message from a known identity
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SendStampSection() {
  const [ref, inView] = useInView<HTMLDivElement>();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value as string);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : 'We could not send the test email. Please try again.'
        );
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section id="send-stamp" className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">

      <div className="mx-auto w-full max-w-[1200px]">
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 items-center">
      

        {/* Mockup */}
        <div
          className={`transition-all duration-700 ${
            inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'
          }`}
        >
          <DeviceMockup />
        </div>

        {/* Form */}
        <div
          className={`transition-all duration-700 ${
            inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-5'
          }`}
          style={{ transitionDelay: '120ms' }}
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4"
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
          >
            Send a real bioStamp to yourself
          </h2>
          <p className="text-base text-foreground-600 leading-relaxed mb-6 max-w-lg">
            Enter your email address below and we'll send a test email with a bioStamp.
          </p>

          <form
            id="playground-test-email"
            data-readdy-form
            onSubmit={handleSubmit}
            className="max-w-lg"
          >
            <label htmlFor="playground-email" className="sr-only">
              Email address
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="playground-email"
                type="email"
                name="email"
                required
                placeholder="you@example.com"
                className="flex-1 px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-full text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <i className="ri-loader-4-line animate-spin text-base" />
                ) : (
                  <i className="ri-send-plane-fill text-base" />
                )}
                Send Test Email
              </button>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            {status === 'success' && (
              <p className="mt-4 text-sm text-accent-700 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-600" />
                Thanks — check your inbox for a test bioStamp email.
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 text-sm text-foreground-800 flex items-center gap-2">
                <i className="ri-error-warning-fill text-primary-600" />
                {formError}
              </p>
            )}
          </form>

          <div className="flex items-start gap-3 mt-7 max-w-lg">
            <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-primary-600">
              <i className="ri-chrome-line text-lg" />
            </div>
            <div>
              <p className="text-sm text-foreground-700 leading-relaxed">
                Install the bioVerifier Chrome extension for the best experience with Gmail and
                Outlook Web.
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mt-1"
              >
                View installation guide
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}